
// vue
import { defineComponent, computed, ref } from "vue";
// composables
import { useI18n } from "@/composables/i18n";
// helpers
import { log } from "@/helpers/env";
//tipos
import {
  Pesaje,
  PesajeData,
} from "@/typings/store/plugins/easyFirestore/pesajes";
import { Chofer } from "@/typings/store/plugins/easyFirestore/choferes";
import { DataEntradaSalida } from "@/typings/components/pesajes/formulario";
//componentes
import SvgIcon from "@/components/custom/SvgIcon.vue";
// validaciones
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default defineComponent({
  components: {
    SvgIcon,
  },
  props: {
    pesaje: {
      default: null,
      type: Object as () => Pesaje | null,
    },
    cargando: {
      default: false,
      type: Boolean,
    },
    arrayChoferes: {
      default: () => [],
      type: Array as () => Chofer[],
    },
    editarGaleria: {
      default: false,
      type: Boolean,
    },
    mostrarGaleria: {
      default: false,
      type: Boolean,
    },
  },
  emits: ["click:guardar", "click:editar-imagenes"],
  setup(props, ctx) {
    const { t } = useI18n();

    const tab = ref(0);
    const menuEntrada = ref(false);
    const menuSalida = ref(false);

    const pesajeData = ref<PesajeData>({});

    const fechaEntrada = computed({
      get: () => {
        const fecha =
          pesajeData.value.fechaEntrada || props.pesaje?.entrada?.fecha;
        return (fecha ? fecha.toDate() : new Date()).format("YYYY-MM-DD");
      },
      set: (value) => {
        const fecha = value.toDate();
        const hoy = new Date();
        fecha.setHours(
          hoy.getHours(),
          hoy.getMinutes(),
          hoy.getSeconds(),
          hoy.getMilliseconds()
        );
        const nuevo = Object.assign({}, pesajeData.value, {
          fechaEntrada: fecha.toISOString(),
        });
        pesajeData.value = nuevo;
      },
    });

    const horaEntrada = computed({
      get: () => {
        const fecha =
          pesajeData.value.fechaEntrada || props.pesaje?.entrada?.fecha;
        return (fecha ? fecha.toDate() : new Date()).format("HH:mm");
      },
      set: (value) => {
        const [hora, minutos] = value.split(":");
        if (hora && hora.length === 2 && minutos && minutos.length === 2) {
          const fechaRaw =
            pesajeData.value.fechaEntrada ??
            (props.pesaje?.entrada?.fecha || "");
          const fecha = fechaRaw?.toDate();
          fecha.setHours(parseInt(hora, 10));
          fecha.setMinutes(parseInt(minutos, 10));
          const nuevo = Object.assign({}, pesajeData.value, {
            fechaEntrada: fecha.toISOString(),
          });
          pesajeData.value = nuevo;
        }
      },
    });

    const pesoEntrada = computed({
      get: () => {
        const peso =
          pesajeData.value.pesoEntrada ?? (props.pesaje?.entrada?.peso || 0);
        return peso;
      },
      set: (value) => {
        const nuevo = Object.assign({}, pesajeData.value, {
          pesoEntrada: value,
        });
        pesajeData.value = nuevo;
      },
    });

    const choferEntrada = computed({
      get: () => {
        const chofer =
          pesajeData.value.choferEntrada ||
          props.pesaje?.entrada?.chofer ||
          null;
        return chofer;
      },
      set: (value) => {
        let chofer: Chofer | null;
        if (typeof value === "string") {
          chofer = {
            nombre: (value as string).toLowerCase(),
            rut: "",
            cargo: "",
            fechaNacimiento: "",
            fechaContratacion: "",
            contacto: "",
            estado: "habilitado",
            foto: null,
          };
        } else {
          chofer = Object.assign({}, value);
        }
        const nuevo = Object.assign({}, pesajeData.value, {
          choferEntrada: chofer,
        });
        pesajeData.value = nuevo;
      },
    });

    const observacionEntrada = computed({
      get: () => {
        const observacion =
          pesajeData.value.observacionEntrada ||
          props.pesaje?.entrada?.observacion ||
          "";
        return observacion;
      },
      set: (value) => {
        const nuevo = Object.assign({}, pesajeData.value, {
          observacionEntrada: value,
        });
        pesajeData.value = nuevo;
      },
    });

    const fechaSalida = computed({
      get: () => {
        const fecha =
          pesajeData.value.fechaSalida || props.pesaje?.salida?.fecha;
        return fecha ? fecha.toDate().format("YYYY-MM-DD") : "";
      },
      set: (value) => {
        const fecha = value.toDate();
        const nuevo = Object.assign({}, pesajeData.value, {
          fechaSalida: fecha.toISOString(),
        });
        pesajeData.value = nuevo;
      },
    });

    const horaSalida = computed({
      get: () => {
        const fecha =
          pesajeData.value.fechaSalida || props.pesaje?.salida?.fecha;
        return fecha ? fecha.toDate().format("HH:mm") : "";
      },
      set: (value) => {
        const [hora, minutos] = value.split(":");
        if (hora && hora.length === 2 && minutos && minutos.length === 2) {
          const fechaRaw =
            pesajeData.value.fechaSalida ?? (props.pesaje?.salida?.fecha || "");
          const fecha = fechaRaw.toDate();
          fecha.setHours(parseInt(hora, 10));
          fecha.setMinutes(parseInt(minutos, 10));
          const nuevo = Object.assign({}, pesajeData.value, {
            fechaSalida: fecha.toISOString(),
          });
          pesajeData.value = nuevo;
        }
      },
    });

    const pesoSalida = computed({
      get: () => {
        const peso =
          pesajeData.value.pesoSalida ?? (props.pesaje?.salida?.peso || 0);
        return peso;
      },
      set: (value) => {
        const peso = value || 0;
        const nuevo = Object.assign({}, pesajeData.value, {
          pesoSalida: peso,
        });
        pesajeData.value = nuevo;
      },
    });

    const choferSalida = computed({
      get: () => {
        const chofer =
          pesajeData.value.choferSalida || props.pesaje?.salida?.chofer || null;
        return chofer;
      },
      set: (value) => {
        let chofer: Chofer | null;
        if (typeof value == "string") {
          chofer = {
            nombre: (value as string).toLowerCase(),
            rut: "",
            cargo: "",
            fechaNacimiento: "",
            fechaContratacion: "",
            contacto: "",
            estado: "habilitado",
            foto: null,
          };
        } else {
          chofer = Object.assign({}, value);
        }
        const nuevo = Object.assign({}, pesajeData.value, {
          choferSalida: chofer,
        });
        pesajeData.value = nuevo;
      },
    });

    const observacionSalida = computed({
      get: () => {
        const observacion =
          pesajeData.value.observacionSalida ||
          props.pesaje?.salida?.observacion ||
          "";
        return observacion;
      },
      set: (value) => {
        const nuevo = Object.assign({}, pesajeData.value, {
          observacionSalida: value,
        });
        pesajeData.value = nuevo;
      },
    });

    const galeriaDisabled = computed((): boolean => {
      return props.editarGaleria && props.mostrarGaleria;
    });

    const state = {
      fechaEntrada,
      horaEntrada,
      pesoEntrada,
      choferEntrada,
      fechaSalida,
      horaSalida,
      pesoSalida,
      choferSalida,
    };

    const rules = {
      fechaEntrada: { required },
      horaEntrada: { required },
      pesoEntrada: { required },
      choferEntrada: { required },
      fechaSalida: { required },
      horaSalida: { required },
      pesoSalida: { required },
      choferSalida: { required },
    };

    const v$ = useVuelidate(rules, state, { $scope: false });

    const erroresFechaEntrada = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.fechaEntrada.$dirty) return errores;
      if (v$.value.fechaEntrada.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.entrada.fecha.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresHoraEntrada = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.horaEntrada.$dirty) return errores;
      if (v$.value.horaEntrada.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.entrada.hora.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresPesoEntrada = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.pesoEntrada.$dirty) return errores;
      if (v$.value.pesoEntrada.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.entrada.peso.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresChoferEntrada = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.choferEntrada.$dirty) return errores;
      if (v$.value.choferEntrada.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.entrada.chofer.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresFechaSalida = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.fechaSalida.$dirty) return errores;
      if (v$.value.fechaSalida.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.salida.fecha.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresHoraSalida = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.horaSalida.$dirty) return errores;
      if (v$.value.horaSalida.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.salida.hora.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresPesoSalida = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.pesoSalida.$dirty) return errores;
      if (v$.value.pesoSalida.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.salida.peso.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const erroresChoferSalida = computed((): Array<string> => {
      const errores: Array<string> = [];
      if (!v$.value.choferSalida.$dirty) return errores;
      if (v$.value.choferSalida.required.$invalid) {
        const error = t(
          "pesajes.formulario.tab.salida.chofer.error-message"
        ) as string;
        errores.push(error);
      }
      return errores;
    });

    const entradaValida = computed((): boolean => {
      return (
        !v$.value.pesoEntrada.required &&
        !v$.value.choferEntrada.required &&
        !v$.value.fechaEntrada.required &&
        !v$.value.horaEntrada.required
      );
    });

    const salidaValida = computed((): boolean => {
      return (
        !v$.value.pesoSalida.required &&
        !v$.value.choferSalida.required &&
        !v$.value.fechaSalida.required &&
        !v$.value.horaSalida.required
      );
    });

    const onClickEditarImagenes = (): void => {
      ctx.emit("click:editar-imagenes");
    };

    const onClickGuardar = (data: DataEntradaSalida): void => {
      ctx.emit("click:guardar", data);
    };

    const clickGuardar = (): void => {
      v$.value.$touch();
      if (entradaValida.value && !salidaValida.value) {
        tab.value = 1;
      }
      if (log) {
        console.log("vuelidate entrada salida editar", v$.value);
      }
      onClickGuardar({
        pesajeData: pesajeData.value,
        formularioValido: !v$.value.$invalid,
      });
    };

    return {
      t,
      v$,
      tab,
      menuEntrada,
      menuSalida,
      fechaEntrada,
      horaEntrada,
      pesoEntrada,
      choferEntrada,
      observacionEntrada,
      fechaSalida,
      horaSalida,
      pesoSalida,
      choferSalida,
      observacionSalida,
      galeriaDisabled,
      erroresFechaEntrada,
      erroresHoraEntrada,
      erroresPesoEntrada,
      erroresChoferEntrada,
      erroresFechaSalida,
      erroresHoraSalida,
      erroresPesoSalida,
      erroresChoferSalida,
      entradaValida,
      salidaValida,
      onClickEditarImagenes,
      onClickGuardar,
      clickGuardar,
    };
  },
});
